'use client';

import { useState, useEffect } from 'react';

interface AudioPlayerProps {
  title: string;
  minutes: number;
  text?: string;
}

export default function AudioPlayer({ title, minutes, text }: AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const togglePlay = () => {
    if (isPlaying) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
    } else {
      setIsPlaying(true);
      window.speechSynthesis.cancel(); // Clear any stuck speech

      const fullText = `You are listening to World Prime Post. ${title}. ${text || ''} ... This concludes the report.`;
      
      // Split text into safe chunks (sentences) for mobile browsers.
      // Mobile Safari/Chrome often fail silently if a single string exceeds 200 words.
      const sentences = fullText.match(/[^.!?]+[.!?]+/g) || [fullText];
      
      sentences.forEach((sentence, index) => {
        const utterance = new SpeechSynthesisUtterance(sentence.trim());
        if (index === sentences.length - 1) {
          utterance.onend = () => setIsPlaying(false);
        }
        utterance.onerror = (e) => {
           console.error("Speech synthesis error", e);
           setIsPlaying(false);
        };
        window.speechSynthesis.speak(utterance);
      });
    }
  };

  return (
    <div 
      onClick={togglePlay}
      className="flex items-center gap-4 p-3 pr-6 bg-muted/20 border border-border/50 rounded-full w-fit cursor-pointer hover:bg-muted/40 transition-colors group"
    >
      <div className={`w-10 h-10 rounded-full text-white flex items-center justify-center shadow-lg transition-all shrink-0 ${isPlaying ? 'bg-red-500 scale-105' : 'bg-brand group-hover:scale-105'}`}>
        {isPlaying ? (
          // Stop icon
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm3 2h8v6H6V7z" clipRule="evenodd" />
          </svg>
        ) : (
          // Play icon
          <svg className="w-4 h-4 ml-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
          </svg>
        )}
      </div>
      <div>
        <span className="block text-xs font-bold uppercase tracking-widest text-foreground">{isPlaying ? 'Playing Summary...' : 'Listen to this report'}</span>
        <span className="block text-[11px] text-muted-foreground">{minutes} min audio summary</span>
      </div>
      <div className={`flex gap-0.5 ml-4 h-4 items-end opacity-60 ${isPlaying ? 'visible' : 'invisible'}`}>
        <div className="w-1 bg-brand/60 h-full animate-[pulse_1.5s_ease-in-out_infinite]" />
        <div className="w-1 bg-brand/60 h-2/3 animate-[pulse_1s_ease-in-out_infinite_0.2s]" />
        <div className="w-1 bg-brand/60 h-1/2 animate-[pulse_1.2s_ease-in-out_infinite_0.4s]" />
        <div className="w-1 bg-brand/60 h-3/4 animate-[pulse_0.8s_ease-in-out_infinite_0.1s]" />
      </div>
    </div>
  );
}
